import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { TeamConvention, TeamConventionStatus } from "@docreview/shared";

const STATUS_BADGE: Record<TeamConventionStatus, string> = {
  pending: "bg-amber-100 text-amber-900 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-slate-100 text-slate-600 border-slate-200",
  revoked: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_LABEL: Record<TeamConventionStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  revoked: "Revoked",
};

export default function AdminConventionsPage() {
  const { user } = useAuth();
  const isAdmin = useAuth().isAdmin === true;

  const [conventions, setConventions] = useState<TeamConvention[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setErr(null);
    try {
      const { conventions } = await api.listConventions();
      setConventions(conventions);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin]);

  const grouped = useMemo(() => {
    return {
      pending: conventions.filter((c) => c.status === "pending"),
      approved: conventions.filter((c) => c.status === "approved"),
      archive: conventions.filter(
        (c) => c.status === "rejected" || c.status === "revoked",
      ),
    };
  }, [conventions]);

  if (!isAdmin) {
    // Non-admins shouldn't be browsing this page directly.
    return <Navigate to="/" replace />;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-4">
      <div className="mb-4">
        <Link
          to="/"
          className="text-sm text-slate-500 hover:text-ink inline-flex items-center gap-1"
        >
          <span>←</span> Deals
        </Link>
      </div>

      <header className="mb-6">
        <h1 className="text-lg font-semibold">Team conventions</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Admin queue for "known team convention" suggestions flagged by attorneys.
          Approved conventions are injected into Claude's system prompt — matched
          findings get cited in the prose review and omitted from the checklist.
          Keep entries narrow and pattern-specific; broad rules make Claude ignore
          real issues.
        </p>
      </header>

      {err && (
        <div className="card p-3 bg-red-50 text-red-700 text-sm mb-4">{err}</div>
      )}
      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="space-y-8">
          <Section title="Pending review" count={grouped.pending.length}>
            {grouped.pending.length === 0 ? (
              <div className="text-sm text-slate-400 italic">
                No pending suggestions.
              </div>
            ) : (
              <ul className="space-y-3">
                {grouped.pending.map((c) => (
                  <PendingCard key={c.id} convention={c} onChanged={refresh} />
                ))}
              </ul>
            )}
          </Section>

          <Section title="Active conventions" count={grouped.approved.length}>
            {grouped.approved.length === 0 ? (
              <div className="text-sm text-slate-400 italic">
                Nothing approved yet. Claude is reviewing without team-specific
                conventions.
              </div>
            ) : (
              <ul className="space-y-3">
                {grouped.approved.map((c, i) => (
                  <ApprovedCard
                    key={c.id}
                    convention={c}
                    index={i + 1}
                    onChanged={refresh}
                  />
                ))}
              </ul>
            )}
          </Section>

          {grouped.archive.length > 0 && (
            <Section title="Rejected / revoked" count={grouped.archive.length}>
              <ul className="space-y-2">
                {grouped.archive.map((c) => (
                  <ArchiveRow key={c.id} convention={c} />
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="font-semibold text-sm mb-2 flex items-center gap-2">
        {title}
        <span className="text-xs font-normal text-slate-400">({count})</span>
      </h2>
      {children}
    </section>
  );
}

function PendingCard({
  convention,
  onChanged,
}: {
  convention: TeamConvention;
  onChanged: () => void;
}) {
  const [title, setTitle] = useState(convention.title);
  const [rationale, setRationale] = useState(convention.rationale);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const dirty =
    title.trim() !== convention.title || rationale.trim() !== convention.rationale;

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api.patchConvention(convention.id, {
        title: title.trim(),
        rationale: rationale.trim(),
      });
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function approve() {
    setBusy(true);
    setErr(null);
    try {
      if (dirty) {
        await api.patchConvention(convention.id, {
          title: title.trim(),
          rationale: rationale.trim(),
        });
      }
      await api.approveConvention(convention.id);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    const reason = window.prompt("Reason for rejection (optional):") ?? "";
    setBusy(true);
    setErr(null);
    try {
      await api.rejectConvention(convention.id, reason || undefined);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <span
          className={`text-xs uppercase tracking-wide px-2 py-0.5 rounded border ${STATUS_BADGE.pending}`}
        >
          {STATUS_LABEL.pending}
        </span>
        <div className="text-xs text-slate-400">
          flagged by {convention.flaggedByName ?? convention.flaggedByEmail ?? "—"}
          {convention.flaggedAt && (
            <> · {new Date(convention.flaggedAt).toLocaleString()}</>
          )}
        </div>
      </div>

      <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
      <input
        className="input mb-2 font-medium"
        value={title}
        onChange={(e) => setTitle(e.target.value.slice(0, 160))}
        maxLength={160}
      />

      <label className="block text-xs font-medium text-slate-600 mb-1">
        Rationale (what Claude reads)
      </label>
      <textarea
        className="input resize-none mb-1"
        rows={4}
        value={rationale}
        onChange={(e) => setRationale(e.target.value.slice(0, 4000))}
        maxLength={4000}
      />
      <div className="text-right text-xs text-slate-400 mb-2">
        {rationale.length} / 4000
      </div>

      {convention.flaggedReason && convention.flaggedReason !== rationale && (
        <details className="text-xs text-slate-500 mb-2">
          <summary className="cursor-pointer hover:text-slate-700">
            Original attorney rationale
          </summary>
          <div className="mt-1 italic whitespace-pre-wrap">{convention.flaggedReason}</div>
        </details>
      )}

      {convention.sourceDealId && convention.sourceReviewId && (
        <div className="text-xs text-slate-400 mb-3">
          From{" "}
          <Link
            className="text-ink hover:underline"
            to={`/deals/${convention.sourceDealId}/reviews/${convention.sourceReviewId}`}
          >
            originating review →
          </Link>
        </div>
      )}

      {err && <div className="text-sm text-red-600 mb-2">{err}</div>}

      <div className="flex justify-end gap-2">
        <button className="btn-secondary text-sm" onClick={reject} disabled={busy}>
          Reject
        </button>
        <button
          className="btn-secondary text-sm"
          onClick={save}
          disabled={busy || !dirty}
        >
          Save edits
        </button>
        <button className="btn-primary text-sm" onClick={approve} disabled={busy}>
          {dirty ? "Save & approve" : "Approve"}
        </button>
      </div>
    </li>
  );
}

function ApprovedCard({
  convention,
  index,
  onChanged,
}: {
  convention: TeamConvention;
  index: number;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(convention.title);
  const [rationale, setRationale] = useState(convention.rationale);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api.patchConvention(convention.id, {
        title: title.trim(),
        rationale: rationale.trim(),
      });
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (
      !window.confirm(
        `Revoke "${convention.title}"? Claude will stop applying this convention to future reviews. Past reviews are unchanged.`,
      )
    )
      return;
    const reason = window.prompt("Reason for revocation (optional):") ?? "";
    setBusy(true);
    setErr(null);
    try {
      await api.revokeConvention(convention.id, reason || undefined);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className={`text-xs uppercase tracking-wide px-2 py-0.5 rounded border ${STATUS_BADGE.approved}`}
          >
            #{index} {STATUS_LABEL.approved}
          </span>
        </div>
        <div className="text-xs text-slate-400">
          approved by {convention.approvedByName ?? convention.approvedByEmail ?? "—"}
          {convention.approvedAt && (
            <> · {new Date(convention.approvedAt).toLocaleDateString()}</>
          )}
        </div>
      </div>

      {editing ? (
        <>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Title
          </label>
          <input
            className="input mb-2 font-medium"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 160))}
            maxLength={160}
          />
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Rationale
          </label>
          <textarea
            className="input resize-none mb-2"
            rows={4}
            value={rationale}
            onChange={(e) => setRationale(e.target.value.slice(0, 4000))}
            maxLength={4000}
          />
          {err && <div className="text-sm text-red-600 mb-2">{err}</div>}
          <div className="flex justify-end gap-2">
            <button
              className="btn-secondary text-sm"
              onClick={() => {
                setTitle(convention.title);
                setRationale(convention.rationale);
                setEditing(false);
              }}
              disabled={busy}
            >
              Cancel
            </button>
            <button className="btn-primary text-sm" onClick={save} disabled={busy}>
              Save
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="font-medium text-sm">{convention.title}</div>
          <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">
            {convention.rationale}
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button
              className="text-xs text-red-600 hover:underline"
              onClick={revoke}
              disabled={busy}
            >
              Revoke
            </button>
            <button
              className="text-xs text-ink hover:underline"
              onClick={() => setEditing(true)}
              disabled={busy}
            >
              Edit
            </button>
          </div>
        </>
      )}
    </li>
  );
}

function ArchiveRow({ convention }: { convention: TeamConvention }) {
  return (
    <li className="card p-3">
      <div className="flex items-center justify-between mb-1">
        <span
          className={`text-xs uppercase tracking-wide px-2 py-0.5 rounded border ${STATUS_BADGE[convention.status]}`}
        >
          {STATUS_LABEL[convention.status]}
        </span>
        <div className="text-xs text-slate-400">
          {convention.rejectedAt
            ? new Date(convention.rejectedAt).toLocaleDateString()
            : convention.revokedAt
              ? new Date(convention.revokedAt).toLocaleDateString()
              : ""}
        </div>
      </div>
      <div className="font-medium text-sm text-slate-600">{convention.title}</div>
      <div className="text-xs text-slate-500 mt-1 italic">
        {convention.rejectedReason || convention.revokedReason || ""}
      </div>
    </li>
  );
}
